import { startHangoutSession, ensureCharacterNames } from './js/hangout/ui/hangout-bootstrap.js';
import { initHangout } from './js/hangout/index.js';
import { storageManager } from './core/storage/index.js';
import { storage } from './js/utils/storage.js';
import { initAuthEventHandler } from './js/utils/authEventHandler.js';
import notificationSystem from './js/utils/UnifiedNotificationSystem.js';
import { normalizeHangoutScenarioConfig } from './js/hangout/shared/scenario-config.js';

const HANGOUT_ENTRYPOINTS = {
  RANKINGS_PROMOTIONAL: 'rankings_promotional',
  RANKINGS_REGULAR_PUBLIC: 'rankings_regular_public',
  RANKINGS_REGULAR_PRIVATE: 'rankings_regular_private',
  RANKINGS_RESUME: 'rankings_resume',
  HANGOUT_PAGE_REFRESH: 'hangout_page_refresh',
  HANGOUT_URL_PARAMS: 'hangout_url_params'
};

const isPageReloadNavigation = () => {
  try {
    const navigationEntries = /** @type {PerformanceNavigationTiming[]} */ (performance.getEntriesByType?.('navigation') || []);
    if (navigationEntries?.length > 0) {
      return navigationEntries[0]?.type === 'reload';
    }
    return performance?.navigation?.type === performance?.navigation?.TYPE_RELOAD;
  } catch {
    return false;
  }
};

const resolveEntrypointFromConfig = ({ config, isPageRefresh, hasConversationId, hasPersistedLocalHistory }) => {
  if (isPageRefresh && (hasConversationId || hasPersistedLocalHistory)) {
    return HANGOUT_ENTRYPOINTS.HANGOUT_PAGE_REFRESH;
  }

  const normalizedEntrypoint = typeof config?.entrypoint === 'string' ? config.entrypoint.trim() : '';
  if (normalizedEntrypoint) {
    return normalizedEntrypoint;
  }
  if (config?.isResuming || (hasConversationId && !config?.isNewConversation)) {
    return HANGOUT_ENTRYPOINTS.RANKINGS_RESUME;
  }
  if (config?.isPromotionalScenario || config?.isTakeMeAnywhereScenario) {
    return HANGOUT_ENTRYPOINTS.RANKINGS_PROMOTIONAL;
  }
  if (config?.isPrivateScenario) {
    return HANGOUT_ENTRYPOINTS.RANKINGS_REGULAR_PRIVATE;
  }
  return HANGOUT_ENTRYPOINTS.RANKINGS_REGULAR_PUBLIC;
};

const showHangoutFeatureNotification = async ({ featureKey, fallbackMessage, delayMs = 0 }) => {
  const userData = storage.get('userData');
  if (!userData?.isLoggedIn) return;
  if ((userData.current_tier || 'free') !== 'free') return;

  const { areHangoutFeatureNotificationsEnabled, isFeatureNotificationDismissed, dismissFeatureNotification } = await import('./js/utils/feature-notifications.js');
  if (!areHangoutFeatureNotificationsEnabled()) return;
  if (await isFeatureNotificationDismissed(featureKey)) return;

  const show = () => {
    const lang = window.getCurrentLanguage ? window.getCurrentLanguage() : 'en';
    const message = window.translations?.[lang]?.hangout?.notifications?.[featureKey] || fallbackMessage;
    const notification = notificationSystem.info(message, {
      duration: 0,
      closable: false,
      position: 'top-right',
      onClick: async () => {
        await dismissFeatureNotification(featureKey);
        notificationSystem.remove(notification);
      }
    });
  };
  if (delayMs) setTimeout(show, delayMs);
  else show();
};

export const showMoanFeatureNotification = async () => {
  await showHangoutFeatureNotification({
    featureKey: 'moan_feature',
    fallbackMessage: 'New feature! She moans on command! Try it, click on her 💕'
  });
};

const showFreeEnergyNotification = async () => {
  await showHangoutFeatureNotification({
    featureKey: 'free_energy',
    fallbackMessage: 'You have 1 character 👤 and 1 scenario 📖 on hold. Go to the Create page to create them!',
    delayMs: 1000
  });
};

function parseUrlConfig() {
  try {
    const params = new URLSearchParams(window.location.search || '');
    const charsParam = params.get('chars');
    const scenarParam = params.get('scenar');

    if (!charsParam || !scenarParam) return null;

    const parsePair = (token) => {
      if (!token) return null;
      const m = String(token).trim().match(/^(\d+)(?:[-:\.](\d+))?$/);
      if (!m) return null;
      const a = Number(m[1]);
      const b = m[2] != null ? Number(m[2]) : undefined;
      return { a, b };
    };

    const characters = [];
    for (const tok of String(charsParam).split(',')) {
      const p = parsePair(tok);
      if (p && p.a != null) characters.push({ id: p.a, outfit: p.b ?? 0 });
    }
    if (characters.length === 0) return null;

    const scenarioMatch = String(scenarParam).trim().match(/^(\d+)/);
    if (!scenarioMatch) return null;
    const scenarioId = scenarioMatch[1];

    const allCharacters = characters.map((c) => ({
      id: String(c.id),
      name: 'Character',
      outfitId: String(c.outfit ?? 0)
    }));

    return {
      scenarioId: String(scenarioId),
      isNewConversation: true,
      conversationId: null,
      greetingVariation: 0,
      greetingVariationCount: 1,
      entrypoint: HANGOUT_ENTRYPOINTS.HANGOUT_URL_PARAMS,
      entryMode: 'new',
      loc_id: null,
      selectedScenario: null,
      allCharacters,
      isMultiCharacter: allCharacters.length > 1,
      characterCount: allCharacters.length,
      primaryCharacterIndex: 0
    };
  } catch (e) {
    console.warn('[Hangout] Failed to parse URL config:', e);
    return null;
  }
}

document.addEventListener('DOMContentLoaded', async () => {
  const { loadingCoordinator } = await import('./core/events/index.js');
  try {
    initAuthEventHandler();
    const { ensureBlockedContentLoaded } = await import('./js/moderation/blockedContentStore.js');
    void ensureBlockedContentLoaded();

    loadingCoordinator.registerDeferredModule('settings-modal', async () => {
        const { createSettingsModal } = await import('./js/hangout/ui/modals/settings-modal.js');
        await createSettingsModal();
    }, {
        priority: 0
    });

    const urlSeedConfig = parseUrlConfig();
    if (urlSeedConfig) {
      try {
        await storageManager.removeItem('currentHangoutConfig');
        await storageManager.setItem('currentHangoutConfig', urlSeedConfig);
      } catch (e) {
        console.warn('[Hangout] Unable to seed config from URL:', e);
      }
    }

    let configFromStorage = await storageManager.getItem('currentHangoutConfig');
    const normalizedScenarioConfig = normalizeHangoutScenarioConfig(configFromStorage);
    if (normalizedScenarioConfig.wasNormalized) {
      configFromStorage = normalizedScenarioConfig.config;
      await storageManager.setItem('currentHangoutConfig', configFromStorage);
    }
    configFromStorage = await ensureCharacterNames(configFromStorage);

    const normalizeId = (v) => (v === undefined || v === null || v === '') ? null : String(v);
    const hasConversationInConfig = !!normalizeId(configFromStorage?.conversationId);
    const { getGlobalHistoryMessages } = await import('./js/hangout/shared/history-resolution.js');
    const hasPersistedLocalHistory = getGlobalHistoryMessages().length > 0;
    const isPageRefresh = isPageReloadNavigation();
    const resolvedEntrypoint = resolveEntrypointFromConfig({
      config: configFromStorage,
      isPageRefresh,
      hasConversationId: hasConversationInConfig,
      hasPersistedLocalHistory
    });

    if (configFromStorage?.entrypoint !== resolvedEntrypoint) {
      configFromStorage = {
        ...(configFromStorage || {}),
        entrypoint: resolvedEntrypoint,
        entryMode: configFromStorage?.entryMode || ((configFromStorage?.isResuming || (hasConversationInConfig && !configFromStorage?.isNewConversation)) ? 'resume' : 'new')
      };
      await storageManager.setItem('currentHangoutConfig', configFromStorage);
    }

    document.title = 'Hangout - Kissandra';

    const hangoutElement = document.getElementById('hangout-container');
    if (!hangoutElement) {
      throw new Error('Hangout container not found');
    }

    await startHangoutSession(configFromStorage, {
      element: hangoutElement,
      isPageRefresh
    });

    showFreeEnergyNotification().catch(err => console.warn('[Hangout] Failed to show free energy notification:', err));
  } catch (error) {
    console.error('[Hangout] Failed to initialize hangout page:', error);
    window.location.replace('/rankings.html');
  }

  loadingCoordinator.markEssentialsLoaded();
});

export default { initHangout };
